import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  GuestInvitationService,
  type GuestPermission,
} from "@/services/guestInvitationService";
import { type ProjectWithAccess } from "@/services/projectService";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { Eye, Edit, Trash2, Users, Loader2 } from "lucide-react";

interface ManageGuestsModalProps {
  project: ProjectWithAccess | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a guest is removed so the parent can refresh guest_count */
  onGuestRemoved?: () => void;
}

export function ManageGuestsModal({
  project,
  isOpen,
  onClose,
  onGuestRemoved,
}: ManageGuestsModalProps) {
  const { user } = useSupabaseAuth();
  const { toast } = useToast();
  const [guests, setGuests] = useState<GuestPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!project || !user) return;
    setLoading(true);
    try {
      const rows = await GuestInvitationService.getHostGuestPermissions(
        user.id,
        project.id,
      );
      setGuests(rows);
    } finally {
      setLoading(false);
    }
  }, [project?.id, user?.id]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  const handleRemove = async (guest: GuestPermission) => {
    if (!user || !project) return;
    setRemovingId(guest.guest_user_id);
    try {
      const result = await GuestInvitationService.removeGuest(
        user.id,
        guest.guest_user_id,
        project.id,
      );
      if (!result.success) {
        toast({
          title: "Failed to remove guest",
          description: result.error ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Guest removed successfully" });
      setGuests((prev) =>
        prev.filter((g) => g.guest_user_id !== guest.guest_user_id),
      );
      onGuestRemoved?.();
    } finally {
      setRemovingId(null);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-emerald-500" />
            Guests — {project.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : guests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No active guests on this project yet.
          </p>
        ) : (
          <ul className="space-y-3 py-2">
            {guests.map((guest) => {
              const email = guest.guest_user?.email ?? guest.guest_user_id;
              const perms = guest.permissions ?? { view: true, edit: false };
              const isRemoving = removingId === guest.guest_user_id;

              return (
                <li
                  key={guest.guest_user_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/50 px-4 py-3"
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-sm font-medium truncate">
                      {email}
                    </span>
                    <div className="flex items-center gap-1">
                      {perms.view && (
                        <Badge
                          variant="outline"
                          className="text-xs text-green-600 border-green-200 bg-green-50 dark:bg-green-900/20"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View
                        </Badge>
                      )}
                      {perms.edit && (
                        <Badge
                          variant="outline"
                          className="text-xs text-blue-600 border-blue-200 bg-blue-50 dark:bg-blue-900/20"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Badge>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={isRemoving}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                    onClick={() => handleRemove(guest)}
                  >
                    {isRemoving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ManageGuestsModal;
